import { useState } from 'react';
import { useCookieConsent, CookiePreferences } from '@/contexts/CookieConsentContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Shield, BarChart3, Megaphone, Sliders } from 'lucide-react';

export const CookiePreferencesDialog = () => {
  const { 
    showPreferencesDialog, 
    closePreferences, 
    cookiePreferences, 
    savePreferences,
    acceptAll,
    rejectAll 
  } = useCookieConsent();

  const [preferences, setPreferences] = useState<CookiePreferences>(cookiePreferences);

  const handleToggle = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return; // Cannot toggle necessary cookies
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    savePreferences(preferences);
  };

  return (
    <Dialog open={showPreferencesDialog} onOpenChange={closePreferences}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cookie Preferences</DialogTitle>
          <DialogDescription>
            Manage your cookie preferences. You can enable or disable different types of cookies below.
            Note that disabling some cookies may affect your experience on our website.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Necessary Cookies */}
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Shield className="h-5 w-5 text-primary mt-1" />
                <div className="space-y-1">
                  <Label className="text-base font-semibold">Necessary Cookies</Label>
                  <p className="text-sm text-muted-foreground">
                    Essential for the website to function properly. These cookies enable core functionality 
                    such as security, network management, and accessibility. They cannot be disabled.
                  </p>
                </div>
              </div>
              <Switch checked={true} disabled />
            </div>
          </div>

          <Separator />

          {/* Analytics Cookies */}
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <BarChart3 className="h-5 w-5 text-primary mt-1" />
                <div className="space-y-1">
                  <Label className="text-base font-semibold">Analytics Cookies</Label>
                  <p className="text-sm text-muted-foreground">
                    Help us understand how visitors interact with our website by collecting and reporting 
                    information anonymously. This helps us improve our website and services.
                  </p>
                </div>
              </div>
              <Switch 
                checked={preferences.analytics} 
                onCheckedChange={() => handleToggle('analytics')}
              />
            </div>
          </div>

          <Separator />

          {/* Marketing Cookies */}
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Megaphone className="h-5 w-5 text-primary mt-1" />
                <div className="space-y-1">
                  <Label className="text-base font-semibold">Marketing Cookies</Label>
                  <p className="text-sm text-muted-foreground">
                    Used to track visitors across websites to display relevant and engaging advertisements. 
                    These cookies help us measure the effectiveness of our marketing campaigns.
                  </p>
                </div>
              </div>
              <Switch 
                checked={preferences.marketing} 
                onCheckedChange={() => handleToggle('marketing')}
              />
            </div>
          </div>

          <Separator />

          {/* Preference Cookies */}
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Sliders className="h-5 w-5 text-primary mt-1" />
                <div className="space-y-1">
                  <Label className="text-base font-semibold">Preference Cookies</Label>
                  <p className="text-sm text-muted-foreground">
                    Enable the website to remember choices you make (such as language or region) 
                    and provide enhanced, personalized features.
                  </p>
                </div>
              </div>
              <Switch 
                checked={preferences.preferences} 
                onCheckedChange={() => handleToggle('preferences')}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={rejectAll} className="w-full sm:w-auto">
            Reject All
          </Button>
          <Button variant="outline" onClick={acceptAll} className="w-full sm:w-auto">
            Accept All
          </Button>
          <Button onClick={handleSave} className="w-full sm:w-auto">
            Save Preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
